// =============================================================================
// Roles & System Role Definitions
// =============================================================================
import { ALL_PERMISSIONS } from './permissions.js';
export const SYSTEM_ROLE_IDS = {
    SUPER_ADMIN: 'super_admin',
    PLATFORM_ADMIN: 'platform_admin',
    SALES_ADMIN: 'sales_admin',
    BILLING_OPS: 'billing_ops',
    SUPPORT_ADMIN: 'support_admin',
    IMPLEMENTATION_MANAGER: 'implementation_manager',
    PORTFOLIO_OWNER: 'portfolio_owner',
    ORG_ADMIN: 'org_admin',
    REGIONAL_DIRECTOR: 'regional_director',
    REGIONAL_REVENUE_MANAGER: 'regional_revenue_manager',
    REGIONAL_MARKETING_DIRECTOR: 'regional_marketing_director',
    CORPORATE_OPS_DIRECTOR: 'corporate_ops_director',
    ASSET_MANAGER: 'asset_manager',
    GENERAL_MANAGER: 'general_manager',
    FRONT_DESK: 'front_desk',
    CONCIERGE: 'concierge',
    MARKETING_MANAGER: 'marketing_manager',
    REVENUE_MANAGER: 'revenue_manager',
    SPA_MANAGER: 'spa_manager',
    FB_MANAGER: 'fb_manager',
    AGENT: 'agent',
};
export const SYSTEM_ROLE_DEFINITIONS = {
    super_admin: {
        id: 'super_admin',
        name: 'Super Admin',
        description: 'LasoXP staff — full system access across all organizations and properties',
        level: 'system',
        permissions: [...ALL_PERMISSIONS],
    },
    platform_admin: {
        id: 'platform_admin',
        name: 'Platform Admin',
        description: 'Full system control — assignable alternative to super_admin for internal staff',
        level: 'system',
        isInternal: true,
        permissions: [...ALL_PERMISSIONS],
    },
    sales_admin: {
        id: 'sales_admin',
        name: 'Sales Admin',
        description: 'Account provisioning, package assignment, and billing oversight',
        level: 'system',
        isInternal: true,
        permissions: ['org:read', 'org:update', 'billing:manage', 'users:invite', 'users:read', 'property:read', 'property:create'],
    },
    billing_ops: {
        id: 'billing_ops',
        name: 'Billing Ops',
        description: 'Billing state management and invoice operations',
        level: 'system',
        isInternal: true,
        permissions: ['org:read', 'billing:manage', 'billing:read', 'property:read'],
    },
    support_admin: {
        id: 'support_admin',
        name: 'Support Admin',
        description: 'Operational visibility without commercial mutation',
        level: 'system',
        isInternal: true,
        permissions: ['org:read', 'property:read', 'users:read', 'users:update', 'chat:read', 'handover:read', 'notifications:read', 'analytics:read'],
    },
    implementation_manager: {
        id: 'implementation_manager',
        name: 'Implementation Manager',
        description: 'Property rollout setup, KB population, and automation configuration',
        level: 'system',
        isInternal: true,
        permissions: ['org:read', 'org:update', 'property:read', 'property:update', 'property:create', 'kb:manage', 'automation:manage', 'users:invite', 'users:read', 'channels:manage'],
    },
    portfolio_owner: {
        id: 'portfolio_owner',
        name: 'Portfolio Owner',
        description: 'Asset owner — cross-org reporting, read-only operations',
        level: 'portfolio',
        permissions: ['portfolio:read', 'portfolio:manage', 'portfolio:reports', 'analytics:read', 'analytics:export', 'analytics:cross_property', 'billing:read', 'billing:manage'],
    },
    org_admin: {
        id: 'org_admin',
        name: 'Organization Admin',
        description: 'Full org management — billing, users, all properties',
        level: 'organization',
        permissions: [
            'org:read', 'org:update', 'org:delete', 'org:manage_users', 'org:manage_roles',
            'billing:read', 'billing:manage',
            'property:read', 'property:update', 'property:manage_settings', 'property:manage_integrations', 'property:manage_widget',
            'chat:read', 'chat:takeover', 'chat:release', 'chat:delete_sessions',
            'kb:read', 'kb:create', 'kb:update', 'kb:delete', 'kb:trigger_scraping',
            'handover:read', 'handover:assign', 'handover:resolve', 'handover:transfer',
            'campaigns:read', 'campaigns:create', 'campaigns:update', 'campaigns:delete', 'campaigns:send',
            'notifications:read', 'notifications:create', 'notifications:manage',
            'analytics:read', 'analytics:export', 'analytics:cross_property',
            'reservations:read', 'reservations:manage',
            'brand:read', 'brand:update', 'brand:manage',
            'users:read', 'users:invite', 'users:update', 'users:remove', 'users:manage_roles',
        ],
    },
    regional_director: {
        id: 'regional_director',
        name: 'Regional Director',
        description: 'Read access across org properties — no user management',
        level: 'organization',
        permissions: ['property:read', 'chat:read', 'kb:read', 'handover:read', 'analytics:read', 'analytics:export', 'analytics:cross_property', 'reservations:read', 'users:read'],
    },
    regional_revenue_manager: {
        id: 'regional_revenue_manager',
        name: 'Regional Revenue Manager',
        description: 'Revenue strategy and analytics across all org properties',
        level: 'organization',
        permissions: ['property:read', 'analytics:read', 'analytics:export', 'analytics:cross_property', 'reservations:read'],
    },
    regional_marketing_director: {
        id: 'regional_marketing_director',
        name: 'Regional Marketing Director',
        description: 'Marketing campaigns and content across all org properties',
        level: 'organization',
        permissions: [
            'property:read',
            'campaigns:read', 'campaigns:create', 'campaigns:update', 'campaigns:delete', 'campaigns:send',
            'notifications:read', 'notifications:create', 'notifications:manage',
            'kb:read', 'kb:create', 'kb:update', 'kb:delete', 'kb:trigger_scraping',
            'analytics:read', 'analytics:export', 'analytics:cross_property',
            'brand:read', 'brand:update',
        ],
    },
    corporate_ops_director: {
        id: 'corporate_ops_director',
        name: 'VP of Operations',
        description: 'Operational oversight across all org properties — chat, handovers, quality',
        level: 'organization',
        permissions: [
            'property:read', 'property:update',
            'chat:read', 'chat:takeover', 'chat:release',
            'kb:read', 'kb:create', 'kb:update',
            'handover:read', 'handover:assign', 'handover:resolve', 'handover:transfer',
            'analytics:read', 'analytics:export', 'analytics:cross_property',
            'reservations:read', 'users:read',
        ],
    },
    asset_manager: {
        id: 'asset_manager',
        name: 'Asset Manager',
        description: 'Financial oversight and reporting — owner representative',
        level: 'organization',
        permissions: ['property:read', 'analytics:read', 'analytics:export', 'analytics:cross_property', 'org:read', 'billing:read', 'billing:manage', 'reservations:read'],
    },
    general_manager: {
        id: 'general_manager',
        name: 'General Manager',
        description: 'Everything at one property',
        level: 'property',
        permissions: [
            'org:read', 'billing:read',
            'property:read', 'property:update', 'property:manage_settings', 'property:manage_integrations', 'property:manage_widget',
            'chat:read', 'chat:takeover', 'chat:release', 'chat:delete_sessions',
            'kb:read', 'kb:create', 'kb:update', 'kb:delete', 'kb:trigger_scraping',
            'handover:read', 'handover:assign', 'handover:resolve', 'handover:transfer',
            'campaigns:read', 'campaigns:create', 'campaigns:update', 'campaigns:delete', 'campaigns:send',
            'notifications:read', 'notifications:create', 'notifications:manage',
            'analytics:read', 'analytics:export',
            'reservations:read', 'reservations:manage',
            'brand:read',
            'users:read', 'users:invite', 'users:update', 'users:remove', 'users:manage_roles',
        ],
    },
    front_desk: {
        id: 'front_desk',
        name: 'Front Desk',
        description: 'Guest chat, handovers, view reservations and knowledge base',
        level: 'property',
        permissions: ['chat:read', 'chat:takeover', 'chat:release', 'handover:read', 'handover:resolve', 'kb:read', 'reservations:read', 'analytics:read'],
    },
    concierge: {
        id: 'concierge',
        name: 'Concierge',
        description: 'Front desk capabilities plus knowledge base management',
        level: 'property',
        permissions: ['chat:read', 'chat:takeover', 'chat:release', 'handover:read', 'handover:resolve', 'kb:read', 'kb:create', 'kb:update', 'reservations:read', 'analytics:read'],
    },
    marketing_manager: {
        id: 'marketing_manager',
        name: 'Marketing Manager',
        description: 'Campaigns, content, analytics, notifications — no guest chat',
        level: 'property',
        permissions: [
            'property:read',
            'campaigns:read', 'campaigns:create', 'campaigns:update', 'campaigns:delete', 'campaigns:send',
            'notifications:read', 'notifications:create', 'notifications:manage',
            'kb:read', 'kb:create', 'kb:update', 'kb:delete', 'kb:trigger_scraping',
            'analytics:read', 'analytics:export',
        ],
    },
    revenue_manager: {
        id: 'revenue_manager',
        name: 'Revenue Manager',
        description: 'Analytics and reservation data for revenue optimization',
        level: 'property',
        permissions: ['property:read', 'billing:read', 'analytics:read', 'analytics:export', 'analytics:cross_property', 'reservations:read'],
    },
    spa_manager: {
        id: 'spa_manager',
        name: 'Spa Manager',
        description: 'Spa knowledge base and reservation access',
        level: 'property',
        permissions: ['kb:read', 'kb:update', 'reservations:read', 'analytics:read', 'handover:read'],
    },
    fb_manager: {
        id: 'fb_manager',
        name: 'F&B Manager',
        description: 'Dining knowledge base and reservation access',
        level: 'property',
        permissions: ['kb:read', 'kb:update', 'reservations:read', 'analytics:read', 'handover:read'],
    },
    agent: {
        id: 'agent',
        name: 'Agent',
        description: 'Guest chat and handover support',
        level: 'property',
        permissions: ['chat:read', 'chat:takeover', 'chat:release', 'handover:read', 'handover:resolve', 'kb:read'],
    },
};
// Backward-compatible: permission-only map derived from definitions
export const DEFAULT_ROLE_PERMISSIONS = Object.fromEntries(Object.entries(SYSTEM_ROLE_DEFINITIONS).map(([id, def]) => [id, def.permissions]));
